import { BaseComponent } from '../../../components/BaseComponent';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { HBox, VBox } from '../../../components/Box';
import { Times } from '../../../components/Icon';
import { Modal, ModalControls } from '../../../components/Modal';
import { FormField } from '../../../components/FormFields';
import { Input } from '../../../components/Input';
import { Button } from '../../../components/Buttons';
import { addAddress, deleteAddress, fetchCountries, updateAddress } from '../actions';
import { selectCountries, selectCompanyPropertyNames } from '../selectors';
import { selectAddressTypes } from '../../Address/selectors';
import { Dropdown } from '../../../components/Dropdown';
import { FieldError } from '../../../components/FieldError';
import { Checkbox } from '../../../components/Checkbox';
import { fetchAddressTypes } from '../../Address/actions';
import ModalComponent from '../../../containers/App/Components/Modal/Modal';
import ButtonComponent from '../../../containers/App/Components/Button/Button';
import CheckboxComponent from '../../../containers/App/Components/Checkbox/Checkbox';
import cn from 'classnames';
import styles from './CompanyAddressModal.module.css';
import { makeSelectLocale } from '../../../containers/LanguageProvider/selectors';
import { translateCountry } from '../translate-countries';

const STATE = {
  edit: null,
  value: '',
  addCompanyAddress: false,
  addValue: '',
  deleteAddressConfirm: null,
  addCountryId: null,
  address: { countryId: null, typeId: 2 },
  fieldErrors: null,
  isChecked: false,
};

const Container = styled.div`
  font-size: 14px;

  .cell:first-child {
    text-transform: capitalize;
  }
`;

/* eslint-disable react/prefer-stateless-function */
class CompanyAddressModalComp extends BaseComponent {
  constructor(props) {
    super(props, { ...STATE });
  }

  componentDidMount() {
    this.props.fetchCountries();
    this.updateAddressAndCheckedState();
  }

  componentDidUpdate(prevProps) {
    // Update state when address or standardId props change
    if (prevProps.address?.id !== this.props.address?.id || 
        prevProps.standardId !== this.props.standardId) {
      this.updateAddressAndCheckedState();
    }
  }

  updateAddressAndCheckedState() {
    if (this.props.isEdit) {
      this.setState({ 
        address: this.props.address, 
        isChecked: String(this.props.standardId) === String(this.props.address?.id) 
      });
    } else {
      this.setState({ 
        address: { typeId: 2 }, 
        isChecked: false 
      });
    }
  }

  reset() {
    this.setState({ ...STATE });
    this.props.toggle();
  }

  get countries() {
    return this.props.countries;
  }

  get locale() {
    return this.props.locale;
  }

  get nameFieldErrors() {
    return this.state.fieldErrors?.name;
  }

  get countryIdFieldErrors() {
    return this.state.fieldErrors?.countryId;
  }

  get cityFieldErrors() {
    return this.state.fieldErrors?.cityId;
  }

  get postcodeFieldErrors() {
    return this.state.fieldErrors?.postcode;
  }

  get addressLine1FieldErrors() {
    return this.state.fieldErrors?.addressLine1;
  }

  addAddress() {
    const companyId = this.props.companyId;
    const addressDto = {
      ...this.state.address,
      isStandardTransportLocation: this.state.isChecked,
    };

    this.props.addAddress(companyId, addressDto, this.handleResponse.bind(this));
  }

  handleResponse(data) {
    let { result, validationErrors } = data;
    if (result === true) {
      return;
    }

    if (validationErrors && validationErrors.length > 0) {
      const errors = data.validationErrors.reduce((prev, curr) => {
        prev[curr.property] = Object.values(curr.constraints);
        return prev;
      }, {});
      this.setState(prev => {
        return { ...prev, fieldErrors: errors };
      });
    }
  }

  updateAddress() {
    const { id, name, postcode, addressLine1, city, countryId, typeId = 2, description } = this.state.address;

    const addressDto = {
      name,
      postcode,
      addressLine1,
      city,
      countryId,
      typeId,
      description,
      isStandardTransportLocation: this.state.isChecked,
    };

    const addressId = id;

    this.props.updateAddress(addressId, addressDto, this.reset.bind(this));
  }

  deleteAddress() {
    const { addressId, companyId } = this.state.deleteAddressConfirm;

    this.props.deleteAddress(addressId, this.reset.bind(this));
  }

  handleCheck = () => {
    this.setState(state => ({ isChecked: !state.isChecked }));
  };

  renderDeleteAddress() {
    if (!this.state.deleteAddressConfirm) return null;
    return (
      <ModalComponent
        className={styles.modal}
        onClose={this.reset.bind(this)}
        title={this.lbl('company.modal.delete.confirm.title')}
      >
        <div className="d-flex gap-2 mt-2">
          <ButtonComponent style="ButtonSoftEdit" onClick={this.deleteAddress.bind(this)}>
            {this.lbl('confirm')}
          </ButtonComponent>
          <ButtonComponent style="ButtonSoftDelete" onClick={this.reset.bind(this)}>
            {this.lbl('cancel')}
          </ButtonComponent>
        </div>
      </ModalComponent>
    );
  }

  onAddressInputChange(evt) {
    const { name, value } = evt.target;
    this.setState((prev, props) => {
      return { ...prev, address: { ...prev.address, [name]: value } };
    });
    return this;
  }

  onAddressCountryChange(evt) {
    const { value } = evt;
    this.setState((prev, props) => {
      return { ...prev, address: { ...prev.address, countryId: value } };
    });
    return this;
  }

  onCommentChange(evt) {
    const { value } = evt.target;
    this.setState(state => ({ address: { ...state.address, description: value } }));
  }

  handleSave = () => {
    if (this.props.isEdit) {
      this.updateAddress();
    } else {
      this.addAddress();
    }
  };

  render() {
    const countriesOptions = this.countries?.map(c => {
      return { value: c.id, label: translateCountry(c.name, this.locale) };
    });
    const selectedCountry = countriesOptions?.find(o => o.value === this.state?.address?.countryId);
    return (
      <>
        <ModalComponent
          key={this.props.address.id ?? 'new_address'}
          onClose={this.reset.bind(this)}
          title={this.lbl('company.details.transportlocaties')}
        >
          <VBox left className="w-100">
            <CheckboxComponent
              label={this.lbl('company.details.stadart')}
              onChange={this.handleCheck}
              checked={this.state.isChecked}
              className={this.state.isChecked && 'green'}
            />

            <label className="form-label">{this.lblStr('company.details.locname')}</label>
            <input
              className="form-control mb-2"
              name="name"
              value={this.state.address?.name ?? ''}
              placeholder={this.lblStr('company.details.locholder')}
              onChange={this.onAddressInputChange.bind(this)}
            />
            <FieldError errors={this.nameFieldErrors} />

            <label className="form-label">{this.lbl('company.details.address')}</label>
            <input
              className="form-control mb-2"
              name="addressLine1"
              value={this.state.address?.addressLine1 ?? ''}
              placeholder={this.lblStr('company.details.addressline')}
              onChange={this.onAddressInputChange.bind(this)}
            />
            <FieldError errors={this.addressLine1FieldErrors} />
            <label className="form-label">{this.lbl('company.details.postcode')}</label>
            <input
              className="form-control mb-2"
              name="postcode"
              value={this.state.address?.postcode ?? ''}
              placeholder={this.lblStr('company.details.postcode')}
              onChange={this.onAddressInputChange.bind(this)}
            />
            <FieldError errors={this.postcodeFieldErrors} />
            <label className="form-label">{this.lbl('company.details.city')}</label>
            <input
              className="form-control mb-2"
              name="city"
              value={this.state.address?.city ?? ''}
              placeholder={this.lblStr('company.details.city')}
              onChange={this.onAddressInputChange.bind(this)}
            />
            <FieldError errors={this.cityFieldErrors} />
            <label className="form-label">Land</label>
            <div className={styles.container}>
              <Dropdown
                className={styles.dropdown}
                name="countryId"
                placeholder="Land"
                options={countriesOptions}
                value={selectedCountry}
                onChange={this.onAddressCountryChange.bind(this)}
              />
            </div>
            <FieldError errors={this.countryIdFieldErrors} />

            <label className="form-label">{this.lbl('company.details.comment')}</label>
            <input
              className="form-control mb-2"
              name="comment"
              type="textarea"
              value={this.state.address.description ?? ''}
              onChange={this.onCommentChange.bind(this)}
            />
          </VBox>

          <div className={styles.buttonContainer}>
            <ButtonComponent style="ButtonSoftEdit" onClick={this.handleSave}>
              Save
            </ButtonComponent>
            {this.props.isEdit && (
              <ButtonComponent
                style="ButtonSoftDelete"
                onClick={this.setStateFn({
                  deleteAddressConfirm: { addressId: this.state.address.id, companyId: this.props.companyId },
                })}
              >
                <Times /> {this.lbl('delete')}
              </ButtonComponent>
            )}
            <ButtonComponent style="ButtonSoftDelete" onClick={this.reset.bind(this)}>
              {this.lbl('cancel')}
            </ButtonComponent>
          </div>
        </ModalComponent>
        {this.renderDeleteAddress()}
      </>
    );

    // <Container>
    //   {this.renderAdd()}
    //   <HBox vCenter>
    //     <h2>{this.lbl('company.details.transportlocaties')}</h2>
    //     <Button small onClick={this.setStateFn({ addCompanyAddress: true })}>{this.lbl('add')}</Button>
    //   </HBox>
    //   { !!this.countries?.length && !!this.addressTypes?.length &&
    //     <VBox>
    //       {company?.addresses?.filter(a => a.typeId === 2)?.map(a => this.renderAddress(a))}
    //     </VBox>
    //   }
    //   {this.renderDeleteAddress()}
    // </Container>
  }
}

CompanyAddressModalComp.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  address: PropTypes.object,
  companyId: PropTypes.number,
  standardId: PropTypes.number,
  toggle: PropTypes.func,
};

const mapStateToProps = (state, ownProps) =>
  createStructuredSelector({
    countries: selectCountries(),
    addressTypes: selectAddressTypes(),
    locale: makeSelectLocale(),
  });

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatch,
    addAddress(companyId, addressDto, callback) {
      dispatch(addAddress(companyId, addressDto, callback));
    },
    updateAddress(addressId, addressDto, callback) {
      dispatch(updateAddress(ownProps.companyId, addressId, addressDto, callback));
    },
    deleteAddress(addressId, callback) {
      dispatch(deleteAddress(ownProps.companyId, addressId, callback));
    },
    fetchCountries() {
      dispatch(fetchCountries());
    },
    fetchAddressTypes() {
      dispatch(fetchAddressTypes());
    },
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export const CompanyAddressModal = compose(withConnect, withRouter, injectIntl)(CompanyAddressModalComp);
